import { useEffect, useState, type MouseEvent, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useVenueAuth } from "../contexts/VenueAuthContext";
import { BarChart3, Gift, Globe, Handshake, QrCode, Scan } from "lucide-react";
import { OfflineBanner } from "@dwelldrop/shared";
import Header from "./Header";
import { ConfirmationModal } from "./ConfirmationModal";

const CREATE_POST_DIRTY_KEY = "dwelldrop_create_post_dirty";

type StaffRole = "owner" | "manager" | "scanner" | "property_operator";

// lucide has no combined "scanner + symbol" icon, so compose a Scan frame
// with a centered glyph ($ for money, P for points).
function ScannerGlyphIcon({ glyph }: { glyph: string }) {
  return (
    <span className="relative inline-flex h-6 w-6 items-center justify-center">
      <Scan className="h-6 w-6" aria-hidden="true" />
      <span className="absolute text-[10px] font-bold leading-none">{glyph}</span>
    </span>
  );
}

// lucide has no combined "QR + chart" icon, so overlay a small bar chart
// on the QR code.
function QrChartIcon() {
  return (
    <span className="relative inline-flex h-5 w-5 items-center justify-center">
      <QrCode className="h-5 w-5" aria-hidden="true" />
      <BarChart3
        className="absolute -bottom-1 -right-1 h-3 w-3"
        aria-hidden="true"
      />
    </span>
  );
}

const NAV_ITEMS: {
  to: string;
  label: string;
  icon: ReactNode;
  allowedRoles: StaffRole[];
}[] = [
  {
    to: "/",
    label: "Dashboard",
    allowedRoles: ["owner", "manager"],
    icon: (
      <svg
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      </svg>
    ),
  },
  {
    to: "/deals",
    label: "My Deals",
    allowedRoles: ["owner", "manager"],
    icon: (
      <svg
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-0zM7 3h5"
        />
      </svg>
    ),
  },
  {
    to: "/scanner",
    label: "Deal Terminal",
    allowedRoles: ["owner", "manager", "scanner"],
    icon: <ScannerGlyphIcon glyph="$" />,
  },
  {
    to: "/rewards",
    label: "Points Terminal",
    allowedRoles: ["owner", "manager", "scanner"],
    icon: <ScannerGlyphIcon glyph="P" />,
  },
  {
    to: "/points/outstanding",
    label: "Outstanding Points",
    allowedRoles: ["owner", "manager"],
    icon: <Gift className="h-5 w-5" aria-hidden="true" />,
  },
  {
    to: "/points/reversals",
    label: "Points Reversals",
    allowedRoles: ["owner", "manager"],
    icon: (
      <svg
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
        />
      </svg>
    ),
  },
  {
    to: "/transactions",
    label: "Transaction Detail",
    allowedRoles: ["owner", "manager"],
    icon: (
      <svg
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"
        />
      </svg>
    ),
  },
  {
    to: "/qr-codes",
    label: "QR Code Manager",
    allowedRoles: ["owner", "manager", "scanner"],
    icon: (
      <svg
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
        />
      </svg>
    ),
  },
  {
    to: "/analytics",
    label: "QR Code Analytics",
    allowedRoles: ["owner", "manager"],
    icon: <QrChartIcon />,
  },
  // {
  //   to: "/referrals",
  //   label: "Referrals",
  //   allowedRoles: ["owner"],
  //   icon: (
  //     <svg
  //       className="h-5 w-5"
  //       fill="none"
  //       viewBox="0 0 24 24"
  //       stroke="currentColor"
  //       aria-hidden="true"
  //     >
  //       <path
  //         strokeLinecap="round"
  //         strokeLinejoin="round"
  //         strokeWidth={2}
  //         d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
  //       />
  //     </svg>
  //   ),
  // },
  {
    to: "/network-deals",
    label: "Network Deals",
    allowedRoles: ["owner", "manager"],
    icon: <Globe className="h-5 w-5" aria-hidden="true" />,
  },
  {
    to: "/saved-deals",
    label: "My Opt-in Deals",
    allowedRoles: ["owner", "manager"],
    icon: <Handshake className="h-5 w-5" aria-hidden="true" />,
  },
  // {
  //   to: "/redemptions/terminal",
  //   label: "Deals Terminal",
  //   allowedRoles: ["owner", "manager"],
  //   icon: (
  //     <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
  //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  //     </svg>
  //   ),
  // },
  {
    to: "/staff",
    label: "Staff",
    allowedRoles: ["owner"],
    icon: (
      <svg
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
  },
  {
    to: "/performance",
    label: "Performance",
    allowedRoles: ["owner", "manager"],
    icon: <BarChart3 className="h-5 w-5" />,
  },
  {
    to: "/deals-analytics",
    label: "Deal Analytics",
    allowedRoles: ["owner", "manager"],
    icon: (
      <svg
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
        />
      </svg>
    ),
  },
  {
    to: "/shortlink-analytics",
    label: "Shortlink Analytics",
    allowedRoles: ["owner", "manager"],
    icon: (
      <svg
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
        />
      </svg>
    ),
  },
  /*
      {
        to: "/ai-studio",
        label: "AI Studio",
        allowedRoles: ["owner"],
        icon: (
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
        ),
      },
      {
        to: "/content",
        label: "Content Manager",
        allowedRoles: ["owner", "manager"],
        icon: (
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        ),
      },
      */
    {
      to: "/invoices",
      label: "Invoices",
      allowedRoles: ["owner"],
      icon: (
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
    },
    {
      to: "/disputes",
      label: "Disputes",
      allowedRoles: ["owner", "manager", "scanner"],
      icon: (
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
    },
    {
      to: "/property-dashboard",
      label: "Dashboard",
      allowedRoles: ["property_operator"],
      icon: (
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      ),
    },
    {
      to: "/property-members",
      label: "Property Members",
      allowedRoles: ["property_operator"],
      icon: (
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
      ),
    },
    {
      to: "/property-qr-codes",
      label: "Property QR Codes",
      allowedRoles: ["property_operator"],
      icon: (
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
          />
        </svg>
      ),
    },
    {
      to: "/property-analytics",
      label: "Property Analytics",
      allowedRoles: ["property_operator"],
      icon: (
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
    },
    {
      to: "/settings",
      label: "Settings",
      allowedRoles: ["owner", "manager", "scanner", "property_operator"],
      icon: (
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
    },
  ];

export default function VenueLayout() {
  const { profile, signOut } = useVenueAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState<
    string | null
  >(null);

  async function handleSignOut() {
    await signOut();
    navigate("/login");
  }

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const updateViewportState = (event: MediaQueryList | MediaQueryListEvent) => {
      const matches = "matches" in event ? event.matches : mediaQuery.matches;
      setIsMobileViewport(matches);
      // Hidden by default on mobile; visible by default on desktop.
      setIsSidebarCollapsed(matches);
    };

    updateViewportState(mediaQuery);
    mediaQuery.addEventListener("change", updateViewportState);

    return () => mediaQuery.removeEventListener("change", updateViewportState);
  }, []);

  const isOwner = profile?.role === "venue_owner";
  const isMallOperator = profile?.role === "property_operator";

  // Derive effective role for menu filtering
  const effectiveRole: StaffRole | null = isMallOperator
    ? "property_operator"
    : isOwner
      ? "owner"
      : (profile?.staffRole ?? null);

  const visibleNavItems = NAV_ITEMS.filter(
    (item) => effectiveRole && item.allowedRoles.includes(effectiveRole)
  );

  function handleNavClick(event: MouseEvent, targetPath: string) {
    event.preventDefault();

    if (
      location.pathname === "/content/new" &&
      location.pathname !== targetPath
    ) {
      const isDirty = localStorage.getItem(CREATE_POST_DIRTY_KEY) === "1";
      if (isDirty) {
        setPendingNavigationPath(targetPath);
        return;
      }
    }

    // Close the drawer after navigating on mobile.
    if (isMobileViewport) {
      setIsSidebarCollapsed(true);
    }

    navigate(targetPath);
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <ConfirmationModal
        isOpen={pendingNavigationPath !== null}
        onClose={() => setPendingNavigationPath(null)}
        onConfirm={() => {
          if (!pendingNavigationPath) return;
          localStorage.removeItem(CREATE_POST_DIRTY_KEY);
          navigate(pendingNavigationPath);
          setPendingNavigationPath(null);
        }}
        title="Unsaved post changes"
        message="You have unsaved post changes. Leave this page anyway?"
        confirmText="Leave"
        cancelText="Stay"
        type="warning"
      />
      {/* Skip to content link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-indigo-600 focus:px-4 focus:py-2 focus:text-white focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
      >
        Skip to content
      </a>

      {/* Fixed Header */}
      <Header
        onToggleSidebar={toggleSidebar}
        isSidebarCollapsed={isSidebarCollapsed}
        isMobileViewport={isMobileViewport}
      />

      {/* Container for sidebar and main content */}
      <div className="flex flex-1 pt-16">
        {/* Backdrop for the mobile drawer */}
        {isMobileViewport && !isSidebarCollapsed && (
          <div
            className="fixed inset-0 top-16 z-30 bg-black/50"
            aria-hidden="true"
            onClick={() => setIsSidebarCollapsed(true)}
          />
        )}
        {/* Sidebar with gradient background */}
        <aside
          className={`fixed left-0 top-16 z-40 h-[calc(100vh-4rem)] w-72 overflow-y-auto bg-gradient-to-b from-gray-900 to-gray-800 text-white transition-transform duration-300 ${isSidebarCollapsed ? "-translate-x-full" : "translate-x-0"
            }`}
          data-testid="sidebar"
        >
          <div className="p-6">
            <nav className="space-y-2">
              {visibleNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid="nav-item"
                  onClick={(event) => handleNavClick(event, item.to)}
                  className={({ isActive }) =>
                    `flex w-full items-center justify-start space-x-3 rounded-lg px-5 py-3 transition-all duration-200 ${isActive
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg"
                      : "text-gray-300 hover:bg-gray-700 hover:text-white"
                    }`
                  }
                >
                  <span data-testid="nav-icon">{item.icon}</span>
                  <span className="font-medium">{item.label}</span>
                </NavLink>
              ))}
            </nav>
            {/* User Profile & Logout */}
            <div className="mt-6 flex flex-wrap rounded-lg border-t border-gray-200 bg-gray-100 p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {profile?.display_name_raw ||
                      profile?.display_name ||
                      "User"}
                  </p>
                  <p className="text-xs capitalize text-gray-500">
                    {profile?.role === "venue_owner"
                      ? "venue owner"
                      : profile?.role === "property_operator"
                        ? "property operator"
                        : profile?.staffRole || "staff"}
                  </p>
                </div>
                <button
                  onClick={handleSignOut}
                  aria-label="Sign out"
                  title="Sign out"
                  className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </aside>

        {/* Main content area */}
        <main
          id="main-content"
          className={`flex-1 overflow-auto transition-all duration-300 ${!isMobileViewport && !isSidebarCollapsed ? "ml-72" : "ml-0"}`}
          tabIndex={-1}
        >
          <OfflineBanner
            title="You're Offline"
            message="Dashboard data may be stale and changes won't be saved while offline. Reconnect to resume managing your venue."
            bannerMessage="You're offline. Dashboard data may be stale and changes won't be saved."
            pillText="You're offline. Dashboard data may be stale and changes won't be saved."
            pillOffsetClass="top-16"
          />
          <div className="p-4 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
